import { Inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { LoginContext } from '@shared/lib/models/login-context';
import { AnalyticsService } from '@shared/lib/services/analytics.service';
import { LoginContextProvider } from '@shared/lib/providers/login-context.provider';
import { LOGIN_CONTEXTS } from '@shared/lib/shared-login.tokens';
import { LOCATION_TOKEN, WINDOW_TOKEN } from '@usana/ux/common';
import { LoginTokens, SsoAuthService, SsoUser } from '@usana/ux/sso-auth';

@Injectable({
  providedIn: 'root',
})
export class LoginContextService {
  private redirectUri: string;

  private readonly redirectUriStorageKey: string = 'ssoLoginRedirectUri';

  private loginTokens: LoginTokens;

  private hasRedirected: boolean;

  private kioskMode: boolean;

  constructor(
    @Inject(LOCATION_TOKEN) private readonly location: Location,
    @Inject(WINDOW_TOKEN) private readonly window: Window,
    @Inject(LOGIN_CONTEXTS) private readonly contexts: LoginContextProvider[],
    private readonly analyticsService: AnalyticsService,
    private readonly ssoAuthService: SsoAuthService
  ) {}

  clearRedirectUri(): void {
    if (!!Storage) {
      sessionStorage.removeItem(this.redirectUriStorageKey);
    }
  }

  storeRedirectUri(uri: string): void {
    this.redirectUri = uri;
    if (!!Storage) {
      sessionStorage.setItem(this.redirectUriStorageKey, this.redirectUri);
    }
  }

  loadRedirectUri(customerType: string): string {
    // if we don't have a redirect uri in memory and we do have storage try to load it
    if (!this.redirectUri && !!Storage) {
      this.redirectUri = sessionStorage.getItem(this.redirectUriStorageKey);
      // cleanup, we only want to use this one time.
      this.clearRedirectUri();
    }
    if (!this.redirectUri || !this.redirectUri.includes(environment.app.domain)) {
      // if its still null
      this.redirectUri = environment.app.redirectUris[customerType] || environment.app.redirectUris.default;
    }

    return this.redirectUri;
  }

  hasRedirectUri(): boolean {
    return !!this.redirectUri;
  }

  // I don't love this but we have to move these outside of the FR sdk logic or else we lose them
  captureLoginTokens(tokens: LoginTokens): void {
    this.loginTokens = tokens;
  }

  redirectPostLogin(): void {
    this.redirectPostLoginWithTokens(this.loginTokens);
  }

  redirectPostLoginWithUser(user: SsoUser): void {
    this.analyticsService.informOfUser(user);
    this.redirectPostLoginWithTokens(this.loginTokens, false);
  }

  redirectPostLoginWithTokens(tokens: LoginTokens, informFullStory: boolean = true): void {
    if (!this.hasRedirected) {
      // its easier to put this case here than to handle the race condition on mobile
      this.hasRedirected = true;
      const provider = this.determineProvider();
      const idToken = tokens?.idToken;
      if (informFullStory) {
        this.analyticsService.informOfIdToken(idToken);
      }
      const customerType =
        this.ssoAuthService.parseIdTokenJwt(idToken)?.customerType ||
        this.ssoAuthService.getUserUnchecked()?.customerType;
      // pulling this value clears it so we need to make sure we pass this in rather than reloading
      const redirectUri = this.loadRedirectUri(customerType);
      if (provider) {
        provider.notifyOfLogin(this.window, tokens, redirectUri);
      } else {
        // default is web
        this.navToUri(redirectUri);
      }
    }
  }

  navigateToRedirectUri(customerType: string): void {
    this.navToUri(this.loadRedirectUri(customerType));
  }

  handleExternalRedirect(url: string): void {
    const provider = this.determineProvider();
    if (provider != null) {
      provider.handleExternalRedirect(this.window, url);
    } else {
      // we are the web app
      this.navToUri(url);
    }
  }

  isMobileAppLogin(): boolean {
    const provider = this.determineProvider();
    if (provider) {
      return provider.isMobile;
    }
    return false;
  }

  setKioskMode(kioskMode: boolean): void {
    this.kioskMode = kioskMode;
  }

  isKioskMode(): boolean {
    return this.kioskMode;
  }

  /**
   * look for the special js variables that are injected by the mobile apps
   */
  determineContext(): LoginContext {
    const provider = this.determineProvider();
    if (provider) {
      return provider.context;
    }
    return LoginContext.WEB;
  }

  private navToUri(uri: string): void {
    this.location.assign(uri);
  }

  private determineProvider(): LoginContextProvider {
    return this.contexts.find((lcp) => lcp.isProvided(this.window));
  }
}
